import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import Terms, { TERMS_VERSION } from './terms';
import { acceptTerms, fetchTermsStatus, type TermsStatus } from './termsApi';
import { useT } from '../i18n/i18n';

/**
 * Asks once, and only once the wording has actually changed.
 *
 * <p>IF THE CHECK ITSELF FAILS, THE PERSON GOES THROUGH. Being unable to reach the server is
 * not evidence that somebody disagreed with anything, and shutting a supervisor out of their
 * learners' records over a failed status call is a worse outcome than a gap in the consent
 * record. Same reasoning as the subscription gate: not asked yet is not the same as refused.
 */
export default function TermsGate({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const t = useT();
  const [status, setStatus] = useState<TermsStatus | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetchTermsStatus()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setUnreachable(true); });
    return () => { cancelled = true; };
  }, [token]);

  async function agree() {
    setBusy(true);
    setError(null);
    try {
      setStatus(await acceptTerms(status?.currentVersion ?? TERMS_VERSION));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  if (unreachable || !token) return <>{children}</>;
  if (!status) return <p className="muted" role="status">{t('Loading…')}</p>;
  if (status.upToDate) return <>{children}</>;

  return (
    <section aria-labelledby="agree-h" style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="card">
        <h2 id="agree-h">
          {status.acceptedVersion ? t('The terms have changed') : t('Before you begin')}
        </h2>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {status.acceptedVersion
            ? t('What changed is below. Please read it and agree to carry on.')
            : t('Please read this once, then carry on.')}
        </p>

        {error && <div className="error" role="alert">{error}</div>}

        <Terms />

        <div className="form-actions">
          <button className="btn" onClick={() => void agree()} disabled={busy}>
            {busy ? t('Saving…') : t('I agree')}
          </button>
        </div>
      </div>
    </section>
  );
}
